/**
 * AdminProfileRemindersPage — approve "complete your profile" reminder emails.
 * Drafts are created for users below a completeness threshold; nothing is sent
 * until approved here.
 */
import React, { useState, useMemo } from 'react';
import useSWR from 'swr';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container, Paper, Typography, Box, Chip, CircularProgress, Alert, Button, Checkbox, Stack,
  Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Link, LinearProgress,
  ToggleButtonGroup, ToggleButton, TextField, Divider, Tooltip,
} from '@mui/material';
import {
  PersonSearch as RemindIcon, Send as SendIcon, Cancel as RejectIcon, Visibility as PreviewIcon,
  AutoAwesome as GenerateIcon,
} from '@mui/icons-material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import BackButton from 'components/common/buttons/BackButton';
import EmailOutboxDialog from './email/EmailOutboxDialog';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const scoreColor = (s) => (s >= 70 ? 'success' : s >= 40 ? 'warning' : 'error');

export default function AdminProfileRemindersPage() {
  const [status, setStatus] = useState('draft');
  const [threshold, setThreshold] = useState(70);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [toast, setToast] = useState(null);
  const [sendReview, setSendReview] = useState(null); // { ids } — drafts to review before sending

  const { data, error, isLoading, mutate } = useSWR(`/profile-reminders/?status=${status}`, fetcher);
  const rows = useMemo(() => data?.results || data || [], [data]);
  const isDraft = status === 'draft';
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const generate = async () => {
    setBusy(true);
    try {
      const r = (await axiosService.post('/profile-reminders/generate/', { threshold })).data;
      setToast(`${r.drafts} new draft(s). ${r.complete_enough} profiles already at ${threshold}% or more; `
        + `${r.recently_reminded} reminded in the last 30 days.`);
      setStatus('draft');
      mutate();
    } catch (e) {
      setToast(e.response?.data?.error || 'Could not generate reminders.');
    } finally {
      setBusy(false);
    }
  };

  const review = async ({ decision, ids }) => {
    setBusy(true);
    try {
      const r = (await axiosService.post('/profile-reminders/review/', { ids, decision })).data;
      setToast(decision === 'approve'
        ? `Sent ${r.sent} reminder(s)${r.failed ? `, ${r.failed} failed` : ''}.`
        : `Rejected ${r.rejected} draft(s). Nothing was sent.`);
      setSelected([]);
      mutate();
    } catch (e) {
      setToast(e.response?.data?.error || 'Review failed.');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const openPreview = async (id) => {
    try {
      setPreview((await axiosService.get(`/profile-reminders/${id}/preview/`, { responseType: 'text' })).data);
    } catch {
      setToast('Could not load the preview.');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <BackButton position="inline" />
        <RemindIcon color="primary" />
        <Typography variant="h4" fontWeight={800}>Profile reminders</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Users with incomplete profiles are rarely found in searches and can't be matched to funding calls.
        Drafts list each user's most valuable missing items and why they matter. Users are reminded at most
        once every 30 days; staff accounts and users with email notifications off are skipped. Nothing is sent
        until you approve.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField size="small" type="number" label="Remind profiles below (%)" value={threshold}
                   onChange={(e) => setThreshold(Number(e.target.value))} inputProps={{ min: 10, max: 100 }}
                   sx={{ width: 200 }} />
        <Button variant="contained" startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <GenerateIcon />}
                onClick={generate} disabled={busy}>
          Generate drafts
        </Button>
      </Paper>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
        <ToggleButtonGroup size="small" exclusive value={status} onChange={(_, v) => { if (v) { setStatus(v); setSelected([]); } }}>
          <ToggleButton value="draft">Drafts</ToggleButton>
          <ToggleButton value="sent">Sent</ToggleButton>
          <ToggleButton value="rejected">Rejected</ToggleButton>
          <ToggleButton value="failed">Failed</ToggleButton>
        </ToggleButtonGroup>
        {isDraft && rows.length > 0 && (
          <>
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={() => setSelected(selected.length === rows.length ? [] : rows.map((r) => r.id))}>
              {selected.length === rows.length ? 'Clear selection' : 'Select all'}
            </Button>
            <Button size="small" variant="contained" color="success" startIcon={<SendIcon />} disabled={!selected.length || busy}
                    onClick={() => setSendReview({ ids: selected })}>
              Review &amp; send ({selected.length})
            </Button>
            <Button size="small" color="error" startIcon={<RejectIcon />} disabled={!selected.length || busy}
                    onClick={() => setConfirm({ decision: 'reject', ids: selected })}>
              Reject
            </Button>
          </>
        )}
      </Stack>

      {error && <Alert severity="error">Failed to load reminders.</Alert>}
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : !rows.length ? (
        <Alert severity="info">{isDraft ? 'No drafts waiting. Generate drafts to start.' : 'Nothing here.'}</Alert>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((r) => (
            <Paper key={r.id} variant="outlined" sx={{ p: 2, borderColor: selected.includes(r.id) ? 'primary.main' : 'divider' }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                {isDraft && <Checkbox checked={selected.includes(r.id)} onChange={() => toggle(r.id)} sx={{ mt: -0.5 }} />}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Link component={RouterLink} to={`/profile/${r.user.id}/`} target="_blank" fontWeight={700}>
                      {r.user.name || r.user.email}
                    </Link>
                    <Typography variant="body2" color="text.secondary">{r.user.email}</Typography>
                    <Typography variant="caption" color="text.secondary">· joined {fmt(r.user.joined)}</Typography>
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, minWidth: 160 }}>
                      <LinearProgress variant="determinate" value={r.score} color={scoreColor(r.score)} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
                      <Typography variant="body2" fontWeight={700}>{r.score}%</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                    {r.missing.map((m) => (
                      <Tooltip key={m.key} title={m.why}><Chip size="small" variant="outlined" label={`Missing: ${m.label}`} /></Tooltip>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button size="small" startIcon={<PreviewIcon />} onClick={() => openPreview(r.id)}>Preview email</Button>
                    {isDraft && (
                      <>
                        <Box sx={{ flex: 1 }} />
                        <Button size="small" color="error" disabled={busy} onClick={() => setConfirm({ decision: 'reject', ids: [r.id], name: r.user.name })}>Reject</Button>
                        <Button size="small" variant="contained" color="success" startIcon={<SendIcon />} disabled={busy}
                                onClick={() => setSendReview({ ids: [r.id] })}>
                          Approve &amp; send
                        </Button>
                      </>
                    )}
                    {r.status === 'sent' && <Typography variant="caption" color="text.secondary">Sent {fmt(r.sent_at)} by {r.reviewed_by_name}</Typography>}
                    {r.status === 'failed' && <Typography variant="caption" color="error">{r.error}</Typography>}
                  </Box>
                </Box>
              </Box>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)}>
        <DialogTitle>{confirm?.decision === 'approve' ? 'Send reminders?' : 'Reject drafts?'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirm?.decision === 'approve'
              ? `${confirm?.name ? `The reminder to ${confirm.name}` : `${confirm?.ids?.length} reminder(s)`} will be emailed now.`
              : `${confirm?.name ? `The draft for ${confirm.name}` : `${confirm?.ids?.length} draft(s)`} will be discarded. Nothing is sent.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color={confirm?.decision === 'approve' ? 'success' : 'error'} disabled={busy}
                  onClick={() => review(confirm)} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
            {confirm?.decision === 'approve' ? 'Send' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle>Email preview</DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          <iframe title="Email preview" srcDoc={preview || ''} sandbox="" style={{ width: '100%', height: '75vh', border: 0 }} />
        </DialogContent>
        <DialogActions><Button onClick={() => setPreview(null)}>Close</Button></DialogActions>
      </Dialog>

      <EmailOutboxDialog
        open={!!sendReview}
        onClose={() => setSendReview(null)}
        title="Profile reminder emails"
        description="Each user gets one reminder listing the missing profile items that matter most for them."
        sendLabel="Approve & send"
        csvName="profile-reminders.csv"
        loadOutbox={async () => (await axiosService.post('/profile-reminders/outbox/', { ids: sendReview.ids })).data}
        loadPreview={async (email) => (await axiosService.get(`/profile-reminders/${email.key}/preview/`, { responseType: 'text' })).data}
        onSend={async (expected) => {
          const r = (await axiosService.post('/profile-reminders/review/', { ids: sendReview.ids, decision: 'approve', expected })).data;
          setToast(`Sent ${r.sent} reminder(s)${r.failed ? `, ${r.failed} failed` : ''}.`);
          setSendReview(null);
          setSelected([]);
          mutate();
        }}
      />

      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)} message={toast} />
    </Container>
  );
}
